import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

const initialFormData = {
  doctorName: "",
  specialization: "",
  hospitalClinic: "",
  pmdcRegistrationNo: "",
  mobileNumber: "",
  email: "",
  address: "",
  city: "",
  referralCommission: "",
  notes: "",
};

const ReferringDoctor = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState<any>("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doctorsData, setDoctorsData] = useState<any>([]);

  const [formData, setFormData] = useState(initialFormData);

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "mobileNumber") {
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, "");

      // Apply Pakistan mobile number format: 03XX-XXXXXXX
      let formattedValue = "";
      if (numericValue.length > 0) {
        formattedValue = numericValue.substring(0, 4);
        if (numericValue.length > 4) {
          formattedValue += "-" + numericValue.substring(4, 11);
        }
      }

      setFormData(prevState => ({
        ...prevState,
        [name]: formattedValue,
      }));
    } else if (name === "referralCommission") {
      // Allow only numbers with up to 2 decimal places
      if (value === "" || /^\d{0,3}(\.\d{0,2})?$/.test(value)) {
        setFormData(prevState => ({
          ...prevState,
          [name]: value,
        }));
      }
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Mobile number validation function
  const isValidMobileNumber = (mobile: string) => {
    // Check if it matches the format 03XX-XXXXXXX (12 characters including dash)
    const mobileRegex = /^03\d{2}-\d{7}$/;
    return mobileRegex.test(mobile);
  };

  // Referral commission validation function (optional, 0 - 100)
  const isValidCommission = (commission: string) => {
    if (commission === "") return true;
    const value = Number(commission);
    return !isNaN(value) && value >= 0 && value <= 100;
  };

  const isFormValid =
    !!formData.doctorName.trim() &&
    !!formData.specialization.trim() &&
    isValidMobileNumber(formData.mobileNumber) &&
    (!formData.email || isValidEmail(formData.email)) &&
    isValidCommission(formData.referralCommission);

  // Empty optional values are sent as null so they are validated as "not provided"
  // on the API and so that clearing a field on update actually clears it.
  const buildPayload = () => ({
    doctorName: formData.doctorName.trim(),
    specialization: formData.specialization.trim(),
    hospitalClinic: formData.hospitalClinic.trim() || null,
    pmdcRegistrationNo: formData.pmdcRegistrationNo.trim() || null,
    mobileNumber: formData.mobileNumber.replace(/-/g, ""), // Remove dash for API
    email: formData.email.trim() || null,
    address: formData.address.trim() || null,
    city: formData.city.trim() || null,
    referralCommission:
      formData.referralCommission === ""
        ? null
        : Number(formData.referralCommission),
    notes: formData.notes.trim() || null,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/referring-doctor/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: debouncedSearch,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDoctorsData(response.data?.data);
      setTotal(response.data?.totalCount);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast.error(error?.response?.data?.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditData(null);
    setFormData(initialFormData);
    setIsFormModalOpen(true);
  };

  const handleFormModalClose = () => {
    if (saving) return;
    setIsFormModalOpen(false);
    setEditData(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async () => {
    const apiData = buildPayload();
    setSaving(true);

    if (editData) {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_LAB_URL}/referring-doctor/updateById/${editData}`,
          apiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData(initialFormData);
          setEditData(null);
          setIsFormModalOpen(false);
          fetchData();
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            "Something went wrong"
        );
      } finally {
        setSaving(false);
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAB_URL}/referring-doctor/addReferringDoctor`,
          apiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData(initialFormData);
          setIsFormModalOpen(false);
          fetchData();
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            "Something went wrong"
        );
      } finally {
        setSaving(false);
      }
    }
  };

  const handleEditClick = (id: any, rowData: any) => {
    setEditData(id);

    // Format mobile number for display (add dash if not present)
    const formatMobileForDisplay = (mobile: string) => {
      if (!mobile) return "";
      const numericOnly = mobile.replace(/\D/g, "");
      if (numericOnly.length >= 4) {
        return numericOnly.substring(0, 4) + "-" + numericOnly.substring(4, 11);
      }
      return numericOnly;
    };

    setFormData({
      doctorName: rowData?.doctorName || "",
      specialization: rowData?.specialization || "",
      hospitalClinic: rowData?.hospitalClinic || "",
      pmdcRegistrationNo: rowData?.pmdcRegistrationNo || "",
      mobileNumber: formatMobileForDisplay(rowData?.mobileNumber || ""),
      email: rowData?.email || "",
      address: rowData?.address || "",
      city: rowData?.city || "",
      referralCommission:
        rowData?.referralCommission === null ||
        rowData?.referralCommission === undefined
          ? ""
          : String(rowData.referralCommission),
      notes: rowData?.notes || "",
    });
    setIsFormModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/referring-doctor/deleteById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchData();
      toast.success(response.data.message);
      setFormData(initialFormData);
      setEditData(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: {
              xs: "1rem",
              sm: "1.25rem",
              md: "1.5rem",
            },
          }}
        >
          Referring Doctors
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            placeholder="Search Doctor Name..."
            variant="outlined"
            value={searchInput}
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{ whiteSpace: "nowrap" }}
          >
            Add Referring Doctor
          </Button>
        </Box>
      </Box>

      <DynamicTable
        headers={[
          "SN",
          "Doctor Name",
          "Specialization",
          "Hospital/Clinic",
          "PMDC Reg. No.",
          "Mobile",
          "Email",
          "City",
          "Commission (%)",
          "Actions",
        ]}
        data={doctorsData?.map((item: any, index: any) => [
          page * rowsPerPage + index + 1,
          item?.doctorName || "",
          item?.specialization || "",
          item?.hospitalClinic || "",
          item?.pmdcRegistrationNo || "",
          item?.mobileNumber || "",
          item?.email || "",
          item?.city || "",
          item?.referralCommission === null ||
          item?.referralCommission === undefined
            ? ""
            : item.referralCommission,
        ])}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        totalRows={total}
        actionButtons={[
          {
            icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
            tooltip: "Edit",
            onClick: (rowIndex: any) =>
              handleEditClick(doctorsData[rowIndex]?._id, doctorsData[rowIndex]),
          },
          {
            icon: <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />,
            tooltip: "Delete",
            onClick: (rowIndex: any) =>
              handleDeleteClick(doctorsData[rowIndex]?._id),
          },
        ]}
        height={"70vh"}
      />
      {doctorsData === null && (
        <Typography
          sx={{
            color: "gray",
            marginTop: "8px",
            textAlign: "center",
            fontSize: "12px",
            fontWeight: "bold",
            marginBottom: "8px",
          }}
        >
          No data found
        </Typography>
      )}

      {/* Add / Update Referring Doctor Modal */}
      <Dialog
        open={isFormModalOpen}
        onClose={handleFormModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="600">
            {editData ? "Update Referring Doctor" : "Add New Referring Doctor"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                className="custom-input"
                label="Doctor Name"
                name="doctorName"
                value={formData.doctorName}
                fullWidth
                required
                autoFocus
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                className="custom-input"
                label="Specialization"
                name="specialization"
                value={formData.specialization}
                fullWidth
                required
                onChange={handleChange}
                placeholder="e.g., Physician, Cardiologist"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                className="custom-input"
                label="Hospital/Clinic"
                name="hospitalClinic"
                value={formData.hospitalClinic}
                fullWidth
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                className="custom-input"
                label="PMDC Registration No."
                name="pmdcRegistrationNo"
                value={formData.pmdcRegistrationNo}
                fullWidth
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Mobile Number"
                className="custom-input"
                name="mobileNumber"
                value={formData.mobileNumber}
                fullWidth
                required
                onChange={handleChange}
                placeholder="0300-1234567"
                inputProps={{
                  maxLength: 12, // 11 digits + 1 dash
                }}
                error={
                  !!(
                    formData.mobileNumber &&
                    !isValidMobileNumber(formData.mobileNumber)
                  )
                }
                helperText={
                  formData.mobileNumber &&
                  !isValidMobileNumber(formData.mobileNumber)
                    ? "Please enter a valid mobile number (e.g., 0300-1234567)"
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Email"
                className="custom-input"
                name="email"
                type="email"
                value={formData.email}
                fullWidth
                onChange={handleChange}
                error={!!(formData.email && !isValidEmail(formData.email))}
                helperText={
                  formData.email && !isValidEmail(formData.email)
                    ? "Please enter a valid email address"
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Address"
                className="custom-input"
                name="address"
                value={formData.address}
                fullWidth
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                className="custom-input"
                label="City"
                name="city"
                value={formData.city}
                fullWidth
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Referral Commission (%)"
                className="custom-input"
                name="referralCommission"
                value={formData.referralCommission}
                fullWidth
                onChange={handleChange}
                placeholder="e.g., 10"
                error={!isValidCommission(formData.referralCommission)}
                helperText={
                  !isValidCommission(formData.referralCommission)
                    ? "Commission must be between 0 and 100"
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Notes"
                className="custom-input"
                name="notes"
                value={formData.notes}
                fullWidth
                multiline
                minRows={2}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleFormModalClose}
            variant="outlined"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!isFormValid || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving
              ? editData
                ? "Updating..."
                : "Saving..."
              : editData
              ? "Update"
              : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default ReferringDoctor;
